<template>
  <SettingsPagePanel v-if="isEditing">
    <SettingsPagePanelHeader :showSubtitle="true">
      <template #title>日程調整カレンダー 編集</template>
      <template #right>
        <div>
          <FlatButton
            :class="$style.button"
            variant="secondary"
            :disabled="isButtonDisabled"
            @click="handleCancel"
          >
            中止
          </FlatButton>
          <FlatButton
            :class="$style.button"
            variant="primary"
            :disabled="isButtonDisabled"
            @click="handleSubmit"
          >
            保存
          </FlatButton>
        </div>
      </template>
    </SettingsPagePanelHeader>
    <CalendarForm v-model="form" />
  </SettingsPagePanel>
  <div v-else>
    <SettingsPagePanel>
      <SettingsPagePanelHeader :showSubtitle="true">
        <template #title>日程調整カレンダー</template>
        <template #right>
          <div>
            <FlatButton
              :class="$style.button"
              variant="caution"
              :disabled="isButtonDisabled"
              @click="handleDelete"
            >
              <template #icon>
                <IconTrash />
              </template>
              削除
            </FlatButton>
            <FlatButton
              :class="$style.button"
              variant="primary"
              :disabled="isEditing"
              @click="handleEdit"
            >
              <template #icon>
                <IconPen />
              </template>
              編集
            </FlatButton>
          </div>
        </template>
      </SettingsPagePanelHeader>
      <template v-if="staffs">
        <CalendarDetail :calendar="form" :staffs="staffs" />
      </template>
    </SettingsPagePanel>
    <SettingsPagePanel :class="$style.bookingsPanel">
      <CalendarBookings :calendarGUID="calendarGUID" />
    </SettingsPagePanel>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { isEqual } from 'lodash-es'

import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPen from '@/components/icons/IconPen.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import type {
  UpdateCalendarInput,
  DeleteCalendarInput,
} from '@/types/schema.generated.ts'
import { useAlert } from '@/libs/alertPortal'
import { useLogger } from '@/logger/logger'
import { captureException } from '@sentry/vue'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'

import type { StaffType } from './calendar.ts'
import {
  type FormType,
  validateForm,
  mapCalendarToForm,
  mapFormToCalendarSettingInput,
} from './formSchema'
import CalendarBookings from './CalendarBookings.vue'
import CalendarDetail from './CalendarDetail.vue'
import CalendarForm from './CalendarForm.vue'
import {
  useFetchCalendarDetailQuery,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
  useSettingsPageCalendarDetailViewStaffsQuery,
  type DeleteCalendarMutation,
} from './SettingsPageCalendarDetailView.generated.ts'
import { defineResponseHandlers } from '@/libs/utilities/graphql.ts'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const alert = useAlert()
const logger = useLogger()
const route = useRoute('calendarSettingDetail')
const router = useRouter()

const calendarGUID = route.params.calendarGUID
const parent = route.matched.at(-2)

/***********************************************
ここから表示データの初期化関連
 ************************************************/
const { result: staffQueryResult } =
  useSettingsPageCalendarDetailViewStaffsQuery({
    // 高速に表示したいのと、極力最新の情報を表示したいので、キャッシュとネットワークの両方から取得
    fetchPolicy: 'cache-and-network',
  })
const staffs = computed<StaffType[] | undefined>(() => {
  if (!staffQueryResult.value?.staffsByPlanner) {
    return undefined
  }

  return staffQueryResult.value?.staffsByPlanner.map(
    (staff): StaffType => ({
      guid: staff.guid,
      displayName: staff.displayName,
      connectedThirdPartyProvider:
        staff.connectedThirdPartyProvider ?? undefined,
    })
  )
})

const { result: calendarDetailQueryResult, refetch: calendarDetailRefetch } =
  useFetchCalendarDetailQuery(
    { guid: calendarGUID },
    { fetchPolicy: 'no-cache' }
  )

// undefined として扱いたくないので dummy データで初期化しておき、
// watchEffect で queryResult が更新されたらその値を使うようにする。
// queryResult が解決されるまで描画はしないので問題無い
const initialForm = ref<FormType>({
  title: '',
  description: '',
  status: 'ACTIVE',
  durationMinutes: 60,
  offsetMinutes: 60,
  assignedStaffGUIDs: [],
  dailyAvailabilities: [],
})
const form = ref<FormType>({ ...initialForm.value })

// キャッシュが残っていても確実に実行されて欲しいので watchEffect を使う
watchEffect(() => {
  if (!calendarDetailQueryResult.value) {
    return
  }

  initialForm.value = mapCalendarToForm(
    calendarDetailQueryResult.value.calendarDetail
  )

  form.value = { ...initialForm.value }
})

/***********************************************
ここから編集関連
 ************************************************/
const isEditing = ref(false)
const handleEdit = () => {
  isEditing.value = true
}

/***********************************************
ここからキャンセル関連
 ************************************************/
const handleCancel = async () => {
  isEditing.value = false
}

/***********************************************
ここから更新関連
 ************************************************/

const { mutate: updateMutate } = useUpdateCalendarMutation()
const { mutate: deleteMutate } = useDeleteCalendarMutation()

const isSubmitting = ref(false)

const handleSubmit = async () => {
  if (isEqual(initialForm.value, form.value)) {
    alert.warning('設定内容に変更ありません')
    return
  }

  isSubmitting.value = true

  const input: UpdateCalendarInput = {
    guid: calendarGUID,
    setting: mapFormToCalendarSettingInput(form.value),
  }

  try {
    logger.ltlPeople.event.updateCalendarSetting(logger.buildMeta(), {
      data: {
        guid: calendarGUID,
        title: form.value.title,
        status: form.value.status,
        description: form.value.description
          ? form.value.description
          : undefined,
        assignedStaffGUIDs: form.value.assignedStaffGUIDs,
        slotDurationMinutes: form.value.durationMinutes,
        slotOffsetMinutes: form.value.offsetMinutes,
        dailyAvailabilities: form.value.dailyAvailabilities,
      },
    })

    const payload = await updateMutate({ input })

    if (payload?.errors || !payload?.data) {
      throw new Error('update calendar failed')
    }

    if (!parent) {
      isSubmitting.value = false
      return
    }

    alert.success('日程調整カレンダーを更新しました。')
    // 更新後は最新状態を再取得してから編集状態を解除して詳細画面に戻す
    await calendarDetailRefetch()
    isEditing.value = false
  } catch (error) {
    captureException(error)
    alert.error('エラーが発生しました。')
  }

  isSubmitting.value = false
  return
}

/***********************************************
ここから削除関連
 ************************************************/

const isDeleting = ref(false)

const handleDelete = async () => {
  if (!window.confirm('本当に削除しますか？')) {
    return
  }

  isDeleting.value = true

  const input: DeleteCalendarInput = {
    guid: calendarGUID,
  }

  try {
    logger.ltlPeople.event.deleteCalendarSetting(logger.buildMeta(), {
      data: {
        guid: calendarGUID,
      },
    })

    const payload = await deleteMutate({ input })

    if (payload?.errors || !payload?.data) {
      throw new Error('delete calendar failed')
    }

    defineResponseHandlers<DeleteCalendarMutation['deleteCalendar'], void>({
      CalendarInUseByScheduleAdjustmentError: () => {
        alert.error(
          'このカレンダーは日程調整デフォルトカレンダーで使用中なので削除できません'
        )
        // 削除ボタンを invalid のままにしたいので、isDeleting は true のままにする
        return
      },
      DeleteCalendarSuccess: async () => {
        if (!parent) {
          isDeleting.value = false
          return
        }

        // eslint-disable-next-line fp/no-mutating-methods
        await router.push({ path: parent.path })
        alert.success('日程調整カレンダーを削除しました。')
        // 削除後は画面遷移するので、isDeleting の変更は不要
      },
    })(payload.data.deleteCalendar)
  } catch (error) {
    captureException(error)
    alert.error('エラーが発生しました。')
    isDeleting.value = false
    return
  }
}

/***********************************************
ここからボタンの有効無効関連
 ************************************************/
const isButtonDisabled = computed(() => {
  const errors = validateForm(form.value)
  return isSubmitting.value || isDeleting.value || errors !== undefined
})

onBeforeRouteLeave((to, from, next) => {
  if (
    isEqual(initialForm.value, form.value) ||
    !isEditing.value ||
    isSubmitting.value ||
    isDeleting.value
  ) {
    next()
    return
  }

  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})
</script>

<style lang="scss" module>
.button {
  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}

.bookingsPanel {
  margin-top: 24px;
}
</style>
